/*
 * Analytics dashboard root.
 *
 * Pulls submissions from `GET /admin/submissions` and renders 11 named
 * sections matching the prototype + master plan §11. Source selection
 * is dropped — production reads only the live API. Auth is gated at
 * the SWA route level (`/analytics/*` requires admin or analyst).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api } from '@shared/api/client';
import {
  crossToolSummary,
  ddwFunnel,
  kpiSummary,
  lensPatterns,
  ownershipBreakdown,
  recentSubmissions,
  redFlagSummary,
  researchByQuestion,
  riskDistributions,
  stayOrGoSummary,
  vendorFrequency,
  isOwnershipRisk,
} from './aggregate';
import type { AnalyticsRow } from './types';
import { fmtDate, fmtMoney, fmtMoneyShort, frictionLabel, flexLabel, relTime } from './format';
import { BarChart } from './components/BarChart';
import { Funnel } from './components/Funnel';
import { Histogram } from './components/Histogram';
import { Modal } from './components/Modal';
import { SectionCard } from './components/SectionCard';
import { SubmissionDetail } from './components/SubmissionDetail';
import './analytics.css';

const PAGE_SIZE = 500;

const FRICTION_LABELS = {
  setup: 'Setup',
  execution: 'Execution',
  handoffs: 'Handoffs',
  closure: 'Closure',
};

const FLEX_LABELS = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

type Status =
  | { kind: 'idle' }
  | { kind: 'loading'; soFar: number }
  | { kind: 'ok'; loadedAt: Date; total: number }
  | { kind: 'empty'; loadedAt: Date }
  | { kind: 'error'; message: string };

export function AnalyticsDashboard() {
  const [rows, setRows] = useState<AnalyticsRow[]>([]);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [detailRow, setDetailRow] = useState<AnalyticsRow | null>(null);
  const reloadIdRef = useRef(0);

  const load = useCallback(async () => {
    const id = ++reloadIdRef.current;
    setStatus({ kind: 'loading', soFar: 0 });
    setRows([]);
    try {
      let offset = 0;
      let total = Number.POSITIVE_INFINITY;
      const collected: AnalyticsRow[] = [];
      // Paginate until we've seen everything (or until total is known
      // and we've matched it). Page size of 500 keeps round-trips low
      // for low-volume tools; high-volume tenants will eventually need
      // server-side aggregation.
      while (collected.length < total) {
        if (id !== reloadIdRef.current) return; // user re-clicked refresh
        const page = await api.getSubmissions({ limit: PAGE_SIZE, offset });
        total = page.total;
        for (const r of page.submissions) collected.push(r as AnalyticsRow);
        if (page.submissions.length === 0) break;
        offset += page.submissions.length;
        setStatus({ kind: 'loading', soFar: collected.length });
      }
      if (id !== reloadIdRef.current) return;
      setRows(collected);
      if (collected.length === 0) {
        setStatus({ kind: 'empty', loadedAt: new Date() });
      } else {
        setStatus({ kind: 'ok', loadedAt: new Date(), total: collected.length });
      }
    } catch (err) {
      if (id !== reloadIdRef.current) return;
      const message =
        err instanceof ApiError
          ? `${err.status} ${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      setStatus({ kind: 'error', message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="analytics-page">
      <div className="analytics-subnav">
        <h1 className="analytics-subnav__title">Analytics</h1>
        <div className="analytics-subnav__nav">
          <a className="analytics-subnav__link" href="/">Tools</a>
          <a className="analytics-subnav__link" href="/admin/ddw">DDW Admin</a>
          <a className="analytics-subnav__link" href="/admin/stay-or-go">Stay or Go? Admin</a>
        </div>
      </div>
      <StatusBar status={status} onReload={load} />
      <main className="analytics-content">
        {status.kind === 'loading' && rows.length === 0 && <LoadingState />}
        {status.kind === 'error' && <ErrorBanner message={status.message} onRetry={load} />}
        {status.kind === 'empty' && <EmptyState onReload={load} />}
        {rows.length > 0 && (
          <DashboardBody rows={rows} onOpenDetail={(row) => setDetailRow(row)} />
        )}
      </main>
      {detailRow && (
        <Modal
          title={`Submission · ${detailRow.tool_id}`}
          onClose={() => setDetailRow(null)}
        >
          <SubmissionDetail row={detailRow} />
        </Modal>
      )}
    </div>
  );
}

// ─── Page chrome ──────────────────────────────────────────────────

function StatusBar({ status, onReload }: { status: Status; onReload: () => void }) {
  let dotClass = '';
  let label = '';
  let lastLoaded = '';
  switch (status.kind) {
    case 'idle':
      label = 'Ready';
      break;
    case 'loading':
      label = `Loading… ${status.soFar > 0 ? `${status.soFar} so far` : ''}`;
      break;
    case 'ok':
      label = `Live · ${status.total} submission${status.total === 1 ? '' : 's'}`;
      lastLoaded = `last loaded ${status.loadedAt.toLocaleTimeString('en-US', { timeZone: 'America/New_York' })}`;
      break;
    case 'empty':
      label = 'Connected · no submissions yet';
      dotClass = 'warn';
      lastLoaded = `last loaded ${status.loadedAt.toLocaleTimeString('en-US', { timeZone: 'America/New_York' })}`;
      break;
    case 'error':
      label = `API error: ${status.message}`;
      dotClass = 'error';
      break;
  }
  return (
    <div className="analytics-statusbar">
      <div className="analytics-statusbar__meta">
        <span className={`analytics-statusbar__dot ${dotClass}`.trim()} />
        <span>{label}</span>
      </div>
      <button
        type="button"
        className="analytics-header__link"
        onClick={onReload}
        disabled={status.kind === 'loading'}
        style={{ color: 'var(--textPrimary)', borderColor: 'var(--border)' }}
      >
        Refresh
      </button>
      {lastLoaded && <span className="analytics-statusbar__last">{lastLoaded}</span>}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="dash-empty" aria-live="polite">
      <h3>Loading submissions…</h3>
      <p>Pulling all rows from the admin endpoint. Page size 500.</p>
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="dash-error" role="alert">
      <h3>Couldn't reach the API</h3>
      <p>
        {message}
        <br />
        <button
          type="button"
          onClick={onRetry}
          style={{
            marginTop: 8,
            padding: '6px 12px',
            border: '1px solid var(--error)',
            borderRadius: 'var(--radius-md)',
            background: 'transparent',
            color: 'var(--error)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </p>
    </div>
  );
}

function EmptyState({ onReload }: { onReload: () => void }) {
  return (
    <div className="dash-empty">
      <h3>No submissions yet</h3>
      <p>
        The admin endpoint is reachable but returned zero rows. Submit any of the three tools at
        least once to populate this dashboard, then refresh.
      </p>
      <button
        type="button"
        onClick={onReload}
        style={{
          padding: '8px 16px',
          background: 'var(--secondary)',
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Refresh
      </button>
    </div>
  );
}

// ─── Body ────────────────────────────────────────────────────────

interface DashboardBodyProps {
  rows: AnalyticsRow[];
  onOpenDetail: (row: AnalyticsRow) => void;
}

function DashboardBody({ rows, onOpenDetail }: DashboardBodyProps) {
  const kpi = useMemo(() => kpiSummary(rows), [rows]);
  const cross = useMemo(() => crossToolSummary(rows), [rows]);
  const funnel = useMemo(() => ddwFunnel(rows), [rows]);
  const lens = useMemo(() => lensPatterns(rows), [rows]);
  const redFlag = useMemo(() => redFlagSummary(rows), [rows]);
  const vendors = useMemo(() => vendorFrequency(rows), [rows]);
  const risk = useMemo(() => riskDistributions(rows), [rows]);
  const ownership = useMemo(() => ownershipBreakdown(rows), [rows]);
  const research = useMemo(() => researchByQuestion(rows), [rows]);
  const recent = useMemo(() => recentSubmissions(rows), [rows]);
  const sg = useMemo(() => stayOrGoSummary(rows), [rows]);

  return (
    <>
      {/* 1. KPI strip */}
      <div className="kpi-strip">
        <div className="kpi">
          <div className="kpi__label">Total submissions</div>
          <div className="kpi__value">{kpi.total}</div>
          <div className="kpi__sub">all-time, all tools</div>
        </div>
        <KpiTile label="Plan Builder" value={kpi.planCount} total={kpi.total} />
        <KpiTile label="Workbench" value={kpi.ddwCount} total={kpi.total} />
        <KpiTile label="Stay or Go?" value={kpi.stayOrGoCount} total={kpi.total} />
        <KpiTile
          label="Scored 1+ vendor"
          value={kpi.ddwScored1Plus}
          total={kpi.ddwCount}
          subText="of Workbench submissions"
        />
        <KpiTile
          label="Research opt-in"
          value={kpi.researchOptIns}
          total={kpi.ddwCount}
          subText="of Workbench submissions"
        />
        <div className="kpi">
          <div className="kpi__label">Last submission</div>
          <div className="kpi__value kpi__value--small">{relTime(kpi.lastSubmittedAt)}</div>
          <div className="kpi__sub">{fmtDate(kpi.lastSubmittedAt)}</div>
        </div>
      </div>

      {/* 2. Cross-tool conversion */}
      {(kpi.planCount > 0 || kpi.ddwCount > 0) && (
        <SectionCard
          title="Cross-tool conversion"
          sub="Buyers who used both tools — measured by shared anonymous client_id across Plan Builder and Workbench."
        >
          <div className="kpi-strip kpi-strip--four">
            <div className="kpi">
              <div className="kpi__label">Plan-only clients</div>
              <div className="kpi__value">{cross.planOnly}</div>
            </div>
            <div className="kpi">
              <div className="kpi__label">Workbench-only clients</div>
              <div className="kpi__value">{cross.ddwOnly}</div>
            </div>
            <div className="kpi">
              <div className="kpi__label">Used both</div>
              <div className="kpi__value kpi__value--accent">{cross.bothClients}</div>
            </div>
            <div className="kpi">
              <div className="kpi__label">Plan → Workbench rate</div>
              <div className="kpi__value">{cross.planToDdwRatePct}%</div>
              <div className="kpi__sub">of plan clients</div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* 3. Workbench funnel */}
      {funnel.length > 0 && (
        <SectionCard
          title="Workbench funnel"
          sub="Where buyers drop off after Phase 1 in the Workbench. Each stage is a subset of the previous."
        >
          <Funnel stages={funnel} />
        </SectionCard>
      )}

      {/* 4 & 5: Lens patterns + red flag */}
      <div className="grid-2">
        <SectionCard title="Lens patterns" sub="What buyers are actually evaluating against.">
          <span className="dash-section__group-label">Friction phase</span>
          <BarChart
            items={lens.friction}
            total={kpi.planCount + kpi.ddwCount}
            labelMap={FRICTION_LABELS}
            emptyMessage="No friction-phase data yet."
          />
          <div style={{ height: 18 }} />
          <span className="dash-section__group-label">Flexibility level</span>
          <BarChart
            items={lens.flex}
            total={kpi.planCount + kpi.ddwCount}
            labelMap={FLEX_LABELS}
            emptyMessage="No flexibility data yet."
          />
          <div style={{ height: 18 }} />
          <span className="dash-section__group-label">Top "want to feel" picks</span>
          <BarChart
            items={lens.feel.slice(0, 6)}
            total={kpi.planCount + kpi.ddwCount}
            emptyMessage="No emotional-feel data yet."
          />
          <div style={{ height: 18 }} />
          <span className="dash-section__group-label">Top "want to stop" picks</span>
          <BarChart
            items={lens.avoid.slice(0, 6)}
            total={kpi.planCount + kpi.ddwCount}
            tone="warn"
            emptyMessage="No emotional-avoid data yet."
          />
        </SectionCard>

        <SectionCard
          title="Red flag fire rate"
          sub={`How often the "you're describing a solution" coaching nudge fires.`}
        >
          <div
            style={{
              display: 'flex',
              gap: 16,
              marginBottom: 16,
              alignItems: 'baseline',
            }}
          >
            <div
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: 'var(--primary)',
                lineHeight: 1,
              }}
            >
              {redFlag.pct}%
            </div>
            <div style={{ fontSize: 12, color: 'var(--textSecondary)' }}>
              of {redFlag.total} submissions had at least one trigger word in their job statement (
              {redFlag.fired} of {redFlag.total})
            </div>
          </div>
          <span className="dash-section__group-label">Top trigger words</span>
          <BarChart
            items={redFlag.topTriggers}
            total={redFlag.total}
            emptyMessage="No trigger words logged in this dataset."
          />
        </SectionCard>
      </div>

      {/* 6. Vendor frequency */}
      <SectionCard
        title={`Vendor frequency · top ${Math.min(vendors.length, 12)}`}
        sub="Vendors actually being scored across submissions. Tells you who buyers are comparing."
      >
        {vendors.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--textMuted)' }}>
            No scored vendors in this dataset yet.
          </p>
        ) : (
          <table className="dash-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th style={{ width: '35%' }}>Frequency</th>
                <th>Avg gut</th>
                <th>Avg adoption</th>
              </tr>
            </thead>
            <tbody>
              {vendors.slice(0, 12).map((v) => {
                const max = vendors[0]?.count ?? 1;
                return (
                  <tr key={v.name}>
                    <td>
                      <strong>{v.name}</strong>
                    </td>
                    <td>
                      <div className="bar-track bar-track--slim">
                        <div
                          className="bar-fill"
                          style={{ width: `${(v.count / max) * 100}%` }}
                          aria-hidden
                        />
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--textMuted)', marginTop: 2 }}>
                        {v.count} submission{v.count === 1 ? '' : 's'}
                      </div>
                    </td>
                    <td>{v.avgGut != null ? `${v.avgGut}/10` : '—'}</td>
                    <td>{v.avgAdoption != null ? `${v.avgAdoption}%` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </SectionCard>

      {/* 7 & 8: Risk distributions + ownership breakdown */}
      <div className="grid-2">
        <SectionCard
          title="Risk distributions"
          sub="Calibrate whether the framework's defaults match the data."
        >
          <span className="dash-section__group-label">
            Buyer's baseline adaptation tax (per submission, scored vendors only)
          </span>
          <Histogram bins={risk.tax} ariaLabel="Buyer baseline adaptation tax distribution" />
          <span className="dash-section__group-label" style={{ marginTop: 14 }}>
            Adoption confidence per scored vendor
          </span>
          <Histogram
            bins={risk.adoption}
            ariaLabel="Adoption confidence distribution per scored vendor"
          />
        </SectionCard>

        <SectionCard
          title="Ownership-type breakdown"
          sub="What ownership types buyers are actually finding — and how the framework rates each on adoption confidence."
        >
          {ownership.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--textMuted)' }}>
              No ownership data in this dataset yet.
            </p>
          ) : (
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Ownership</th>
                  <th style={{ width: '40%' }}>Frequency</th>
                  <th>Avg adoption</th>
                </tr>
              </thead>
              <tbody>
                {ownership.map((o) => {
                  const max = ownership[0]?.count ?? 1;
                  return (
                    <tr key={o.key}>
                      <td>
                        <strong>{o.label}</strong>
                      </td>
                      <td>
                        <div className="bar-track bar-track--slim">
                          <div
                            className={`bar-fill ${isOwnershipRisk(o.key) ? 'bar-fill--warn' : ''}`}
                            style={{ width: `${(o.count / max) * 100}%` }}
                            aria-hidden
                          />
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--textMuted)', marginTop: 2 }}>
                          {o.count} vendor{o.count === 1 ? '' : 's'}
                        </div>
                      </td>
                      <td>{o.avgAdoption != null ? `${o.avgAdoption}%` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>

      {/* 9. Research answers */}
      <SectionCard
        title={`Research answers · ${research.totalOptIns} opt-in${research.totalOptIns === 1 ? '' : 's'}`}
        sub="Anonymous qualitative responses, grouped by question."
      >
        {research.totalOptIns === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--textMuted)' }}>
            No research opt-ins in this dataset yet.
          </p>
        ) : (
          (['q1', 'q2', 'q3'] as const).map((qk) => {
            const block = research[qk];
            return (
              <div className="qa-block" key={qk}>
                <div className="qa-block__question">
                  {block.label} · {block.entries.length}
                </div>
                {block.entries.length === 0 ? (
                  <p className="qa-block--empty">— no answers —</p>
                ) : (
                  block.entries.map((entry, i) => (
                    <div className="qa-block__answer" key={i}>
                      {entry.answer}
                      <div className="qa-block__answer-meta">{relTime(entry.date)}</div>
                    </div>
                  ))
                )}
              </div>
            );
          })
        )}
      </SectionCard>

      {/* 11 (NEW). Stay or Go? distribution */}
      {sg.total > 0 && (
        <SectionCard
          title={`Stay or Go? · ${sg.total} submission${sg.total === 1 ? '' : 's'}`}
          sub="How buyers are landing on stay-vs-switch decisions and what staying costs them."
        >
          <div className="kpi-strip kpi-strip--three">
            <div className="kpi">
              <div className="kpi__label">Avg Stay Score</div>
              <div className="kpi__value">{sg.avgStayScore ?? '—'}/10</div>
            </div>
            <div className="kpi">
              <div className="kpi__label">Avg cost of staying (24mo)</div>
              <div className="kpi__value kpi__value--small">
                {fmtMoneyShort(sg.avgCostOfStaying)}
              </div>
              <div className="kpi__sub">{fmtMoney(sg.avgCostOfStaying)}</div>
            </div>
            <div className="kpi">
              <div className="kpi__label">Distinct verdicts</div>
              <div className="kpi__value">{sg.byVerdict.length}</div>
            </div>
          </div>
          <div style={{ height: 14 }} />
          <span className="dash-section__group-label">Verdict distribution</span>
          <BarChart
            items={sg.byVerdict}
            total={sg.total}
            emptyMessage="No verdicts captured yet."
          />
          {sg.byTier.length > 0 && (
            <>
              <div style={{ height: 14 }} />
              <span className="dash-section__group-label">Spend tier distribution</span>
              <BarChart items={sg.byTier} total={sg.total} tone="accent" />
            </>
          )}
        </SectionCard>
      )}

      {/* 10. Recent submissions */}
      <SectionCard
        title={`Recent submissions · last ${recent.length}`}
        sub="Click any row to see the full lens, vendors, research answers, and raw payload."
      >
        <table className="dash-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Source</th>
              <th>Job statement</th>
              <th>Friction</th>
              <th>Flex</th>
              <th>Vendors</th>
              <th>Research</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((r) => (
              <tr
                key={r.row.submission_id}
                className="row-clickable"
                tabIndex={0}
                onClick={() => onOpenDetail(r.row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenDetail(r.row);
                  }
                }}
              >
                <td>{relTime(r.row.submitted_at)}</td>
                <td>
                  <span className={`inline-pill inline-pill--${toneClass(r.toolTone)}`}>
                    {r.toolLabel}
                  </span>
                </td>
                <td className="truncate">{r.summary}</td>
                <td>{r.friction === '—' ? '—' : frictionLabel(r.friction)}</td>
                <td>
                  {r.row.tool_id === 'stay_or_go' ? r.flex : flexLabel(r.flex === '—' ? null : r.flex)}
                </td>
                <td>
                  {r.vendorsScored == null ? '—' : <strong>{r.vendorsScored}</strong>}
                </td>
                <td>
                  {r.hasResearch ? (
                    <span className="inline-pill inline-pill--success">opted in</span>
                  ) : (
                    <span className="inline-pill">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </>
  );
}

function toneClass(tone: 'success' | 'warning' | 'primary' | 'neutral'): string {
  switch (tone) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warn';
    case 'primary':
      return 'accent';
    default:
      return '';
  }
}

interface KpiTileProps {
  label: string;
  value: number;
  total: number;
  subText?: string;
}

function KpiTile({ label, value, total, subText }: KpiTileProps) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="kpi">
      <div className="kpi__label">{label}</div>
      <div className="kpi__value">
        {value}
        <span className="kpi__value-suffix">{` · ${pct}%`}</span>
      </div>
      {subText && <div className="kpi__sub">{subText}</div>}
    </div>
  );
}
